import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Button } from "@accredo/fe-shared/theme/components";
import { useBankAccountActions } from "hooks/useBankAccountActions.jsx";

const PAGE_SIZE = 25;

/**
 * Replacements are listed so the user can see what gets overwritten, but a
 * selection can run to hundreds — so the list is truncated for display while the
 * count stays exact.
 */
const MAX_LISTED_REPLACEMENTS = 20;

/**
 * Assign many objects to one bank account.
 *
 * Doing this one object at a time is fine for later corrections but not for the
 * initial pass, which can span hundreds of objects.
 *
 * Two things drive the design:
 * - Linking *replaces* whatever account an object had, so a confirmation step
 *   lists those replacements with the IBAN being replaced before anything is
 *   written. The list comes from a server-side dry run, so the preview cannot
 *   disagree with what actually gets saved.
 * - "Select all" means every object matching the search, not just the visible
 *   page — otherwise you assign 25 of 200 and believe you are done. The ids are
 *   fetched and held explicitly, keeping the write auditable.
 */
export default function BulkAssignObjectsDialog({
  open,
  onClose,
  bankAccount,
  onAssigned,
}) {
  const { fetchAssignableObjects, batchAssignObjects } =
    useBankAccountActions();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [objects, setObjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const loadPage = async () => {
    setIsLoading(true);
    await fetchAssignableObjects(
      bankAccount.id,
      { search, page, limit: PAGE_SIZE },
      (res) => {
        setObjects(res?.data ?? []);
        setTotal(res?.total ?? 0);
      }
    );
    setIsLoading(false);
  };

  useEffect(() => {
    if (open) loadPage();
  }, [open, search, page]);

  const toggle = (objekt_id) =>
    setSelectedIds((current) =>
      current.includes(objekt_id)
        ? current.filter((id) => id !== objekt_id)
        : [...current, objekt_id]
    );

  const selectAllMatching = async () => {
    setIsLoading(true);
    await fetchAssignableObjects(
      bankAccount.id,
      { search, ids_only: 1 },
      (res) => setSelectedIds(res?.data ?? [])
    );
    setIsLoading(false);
  };

  /** Ask the backend what would happen, without writing. */
  const requestPreview = async () => {
    setIsSaving(true);
    const result = await batchAssignObjects(bankAccount.id, selectedIds, true);
    setIsSaving(false);
    if (result?.data) setPreview(result.data);
  };

  const confirm = async () => {
    setIsSaving(true);
    const result = await batchAssignObjects(bankAccount.id, selectedIds, false);
    setIsSaving(false);

    if (result) {
      onAssigned?.();
      handleClose();
    }
  };

  const handleClose = () => {
    setSearch("");
    setPage(1);
    setSelectedIds([]);
    setPreview(null);
    onClose();
  };

  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Objekte zuordnen
        <Typography variant="body2" color="text.secondary">
          Schadenzahlungen der gewählten Objekte gehen künftig an{" "}
          <strong>{bankAccount?.iban}</strong>.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {preview ? (
          <AssignmentPreview preview={preview} />
        ) : (
          <>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                label="Objekte suchen"
                size="small"
                value={search}
                onChange={(event) => {
                  setPage(1);
                  setSearch(event.target.value);
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button variant="tertiary" onClick={selectAllMatching}>
                Alle {total} Treffer auswählen
              </Button>
              <Button
                variant="tertiary"
                onClick={() => setSelectedIds([])}
                disabled={selectedIds.length === 0}
              >
                Auswahl aufheben
              </Button>
            </Stack>

            <Typography variant="body2" sx={{ mb: 1 }}>
              {selectedIds.length} von {total} Objekten ausgewählt
            </Typography>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Bezeichnung</TableCell>
                    <TableCell>Adresse</TableCell>
                    <TableCell>Aktuelle Bankverbindung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {objects.map((objekt) => (
                    <TableRow key={objekt.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(objekt.id)}
                          onChange={() => toggle(objekt.id)}
                          inputProps={{
                            "aria-label": `Objekt ${objekt.bezeichnung} auswählen`,
                          }}
                        />
                      </TableCell>
                      <TableCell>{objekt.bezeichnung}</TableCell>
                      <TableCell>
                        {[objekt.strasse, objekt.hausnr, objekt.plz, objekt.ort]
                          .filter(Boolean)
                          .join(" ")}
                      </TableCell>
                      <TableCell>
                        {objekt.is_linked_to_this_account ? (
                          <Chip
                            size="small"
                            color="success"
                            label="Dieses Konto"
                          />
                        ) : objekt.current_bank_account_iban ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={objekt.current_bank_account_iban}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="tertiary" onClick={handleClose}>
          Abbrechen
        </Button>

        {preview ? (
          <>
            <Button variant="tertiary" onClick={() => setPreview(null)}>
              Zurück zur Auswahl
            </Button>
            <Button variant="primary" onClick={confirm} disabled={isSaving}>
              Zuordnung bestätigen
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            onClick={requestPreview}
            disabled={selectedIds.length === 0 || isSaving}
          >
            Weiter ({selectedIds.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * The confirmation step. Replacements are called out separately because they are
 * the destructive part: those objects already had a different account.
 */
function AssignmentPreview({ preview }) {
  const { linked = [], replaced = [], unchanged = [], skipped = [] } = preview;

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {linked.length} Objekte werden neu verknüpft, {unchanged.length} sind
        bereits diesem Konto zugeordnet.
      </Typography>

      {replaced.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>
            {replaced.length} Objekte haben bereits eine andere Bankverbindung
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Für diese Objekte wird die bisherige Bankverbindung ersetzt.
            Schadenzahlungen gehen danach an das neue Konto.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Objekt</TableCell>
                <TableCell>Bisherige Bankverbindung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {replaced.slice(0, MAX_LISTED_REPLACEMENTS).map((entry) => (
                <TableRow key={entry.objekt_id}>
                  <TableCell>{entry.bezeichnung}</TableCell>
                  <TableCell>{entry.previous_iban}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {replaced.length > MAX_LISTED_REPLACEMENTS && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              … und {replaced.length - MAX_LISTED_REPLACEMENTS} weitere.
            </Typography>
          )}
        </Alert>
      )}

      {skipped.length > 0 && (
        <Alert severity="info">
          <AlertTitle>{skipped.length} Objekte werden übersprungen</AlertTitle>
          <Typography variant="body2">
            Diese Objekte gehören nicht zum Kunden dieser Bankverbindung.
          </Typography>
        </Alert>
      )}
    </Stack>
  );
}
